import { PreviewImageModal } from '@exam-center/components';
import {
  GetExamEventExamineeByIdDocument,
  SearchExamineeStatusesInExamEventDocument,
  useGetExamEventExamineeByIdQuery,
  useRegisterExamineeWithAlternativeDocumentMutation,
} from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod'; // can't use shared/utils resolver, since it's not inherit output type
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Loading,
  Box,
  useTheme,
} from '@shared/components';
import { useDisclosure, useMediaQuery } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { applyWatermark } from '@shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  type AlternativeRegistrationErrorStateType,
  Camera,
  type CameraRef,
} from '../../components';
import { useUploadAlternativeDocumentImage } from '../../hooks';
import {
  type AlternativeRegistrationFormInput,
  type AlternativeRegistrationFormOutput,
  AlternativeRegistrationSchema,
} from '../../schemas';

import { AlternativeRegistrationModalLogicAction } from './action';
import { AlternativeRegistrationModalLogicContent } from './content';
import { AlternativeRegistrationModalLogicTitle } from './title';
import { convertStringToNameTitle, convertToRegistrationType } from './utils';

const MAX_CAMERA_ACCESS_ATTEMPT = 3;

export type AlternativeRegistrationModalLogicState =
  | 'form'
  | 'image-capturing'
  | 'image-confirmation'
  | 'error';

interface AlternativeRegistrationModalLogicProps {
  open: boolean;
  onClose: () => void;
  examEventExamineeId: string;
}

export default function AlternativeRegistrationModalLogic(
  props: Readonly<AlternativeRegistrationModalLogicProps>,
) {
  const { open: isOpen, onClose, examEventExamineeId } = props;

  const { showPromiseSnackbar } = useSnackbar();

  const theme = useTheme();
  const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('tablet'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const [isImagePreviewerModalOpen, imagePreviewerModalHandlers] = useDisclosure();
  const { open: openImagePreviewerModal, close: closeImagePreviewerModal } =
    imagePreviewerModalHandlers;

  const cameraRef = useRef<CameraRef>(null);
  // Holds the most recent capture until it is confirmed. Promoted to
  // `documentImageBlob` only on confirm, so it can never diverge from the
  // displayed `documentImage`.
  const capturedImageBlobRef = useRef<Blob | null>(null);

  const [modalState, setModalState] = useState<AlternativeRegistrationModalLogicState>('form');
  const [image, setImage] = useState<string | undefined>(undefined);
  // The confirmed document image binary, uploaded on submit. Kept in state
  // (rather than derived from the object URL) because CSP connect-src blocks
  // fetching blob: URLs.
  const [documentImageBlob, setDocumentImageBlob] = useState<Blob | undefined>(undefined);
  const [isCameraError, setIsCameraError] = useState(false);
  const [cameraAccessAttempt, setCameraAccessAttempt] = useState(1);
  const [alternativeRegistrationError, setAlternativeRegistrationError] = useState<
    AlternativeRegistrationErrorStateType | undefined
  >(undefined);

  const [registerExamineeWithAlternativeDocument] =
    useRegisterExamineeWithAlternativeDocumentMutation();
  const { uploadAlternativeDocumentImage, loading: isUploadingImage } =
    useUploadAlternativeDocumentImage();

  const { data: examEventExamineeData, loading: isGettingExamEventExaminee } =
    useGetExamEventExamineeByIdQuery({
      skip: !isOpen || examEventExamineeId === '',
      variables: {
        getExamEventExamineeByIdInput: {
          id: examEventExamineeId,
        },
      },
    });
  const examEventExaminee =
    examEventExamineeData?.ecGetExamEventExamineeById.examEventExaminee.examinee;

  const formContext = useForm<
    AlternativeRegistrationFormInput,
    unknown,
    AlternativeRegistrationFormOutput
  >({
    defaultValues: {
      nameTitle: null,
      firstName: '',
      middleName: '',
      lastName: '',
      documentType: '',
      documentImage: '',
      cause: '',
    },
    resolver: zodResolver(AlternativeRegistrationSchema),
  });

  useEffect(() => {
    if (!isGettingExamEventExaminee && examEventExaminee) {
      const currentNameTitle = convertStringToNameTitle(examEventExaminee.title);
      formContext.setValue('firstName', examEventExaminee.firstName);
      formContext.setValue('middleName', examEventExaminee.middleName ?? '');
      formContext.setValue('lastName', examEventExaminee.lastName);
      formContext.setValue('nameTitle', currentNameTitle);
    }
  }, [examEventExaminee, formContext, isGettingExamEventExaminee]);

  // Keep the confirmed blob coupled to the form field: if the image is cleared
  // (e.g. the reset button in FormImageInput), drop the stale blob too.
  useEffect(() => {
    const subscription = formContext.watch((value, { name }) => {
      if (name === 'documentImage' && !value.documentImage) {
        setDocumentImageBlob(undefined);
        capturedImageBlobRef.current = null;
      }
    });
    return () => subscription.unsubscribe();
  }, [formContext]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleExitedDialog = () => {
    if (cameraRef.current) {
      cameraRef.current.stopCamera();
    }
    setModalState('form');
    // Revoke both the transient capture URL and the confirmed one (they differ
    // after a retake that was not re-confirmed).
    const confirmedImageUrl = formContext.getValues('documentImage');
    if (image) {
      URL.revokeObjectURL(image);
    }
    if (confirmedImageUrl && confirmedImageUrl !== image) {
      URL.revokeObjectURL(confirmedImageUrl);
    }
    setImage(undefined);
    setDocumentImageBlob(undefined);
    capturedImageBlobRef.current = null;
    formContext.reset();
  };

  const handleStartCamera = async () => {
    if (cameraAccessAttempt < MAX_CAMERA_ACCESS_ATTEMPT) {
      setIsCameraError(false);
      setCameraAccessAttempt((prev) => prev + 1);
    }
    if (cameraRef.current) {
      await cameraRef.current.startCamera();
    }
  };

  const handleCaptureImage = async () => {
    if (cameraRef.current) {
      await cameraRef.current.capture();
    }
  };

  const handleRetake = async () => {
    if (cameraRef.current) {
      cameraRef.current.resetCamera();
      await cameraRef.current.startCamera();
    }
  };

  const handleRegisterWithAlternativeDocument = async (data: AlternativeRegistrationFormOutput) => {
    const isValid = await formContext.trigger();
    if (isValid) {
      showPromiseSnackbar({
        message: {
          success: 'ลงทะเบียนผู้เข้าสอบเรียบร้อยแล้ว',
          error: 'ไม่สามารถลงทะเบียนผู้เข้าสอบได้',
          pending: 'กำลังลงทะเบียนผู้เข้าสอบ',
        },
        promise: async () => {
          if (!documentImageBlob) {
            throw new Error('Missing document image');
          }

          const { fileName } = await uploadAlternativeDocumentImage({
            examEventExamineeId,
            imageBlob: documentImageBlob,
          });

          await registerExamineeWithAlternativeDocument({
            variables: {
              registerExamineeWithAlternativeDocumentInput: {
                examEventExamineeId,
                fileName: fileName,
                firstname: data.firstName,
                middlename: data.middleName || null,
                lastname: data.lastName,
                title: data.nameTitle as string,
                reason: data.cause,
                registrationDocType: convertToRegistrationType(data.documentType),
              },
            },
            onError: (error) => {
              setModalState('error');

              if (error.message.includes('is absent')) {
                setAlternativeRegistrationError('user-absent');
              }

              if (error.message.includes('is pending eKYC approval')) {
                setAlternativeRegistrationError('e-kyc-pending-approval');
              }

              if (error.message.includes('User is blocked')) {
                setAlternativeRegistrationError('user-blocked');
              }

              if (error.message.includes('is canceled')) {
                setAlternativeRegistrationError('user-canceled');
              }

              throw error; // throw error to interrupt form submission
            },
            refetchQueries: [
              SearchExamineeStatusesInExamEventDocument,
              GetExamEventExamineeByIdDocument,
            ],
            awaitRefetchQueries: true,
          });

          handleClose();
        },
      });
    }
  };

  const handleFormSubmit = async () => {
    await formContext.handleSubmit(handleRegisterWithAlternativeDocument)();
  };

  useEffect(() => {
    handleClose();
  }, [handleClose, isDesktop, isTabletOrDesktop]);

  return (
    <>
      {/* Form Modal */}
      <Dialog
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          transition: {
            onExited: handleExitedDialog,
          },
        }}
        fullScreen={!isTabletOrDesktop}
      >
        <DialogTitle onClose={handleClose}>
          <AlternativeRegistrationModalLogicTitle
            state={modalState}
            onBack={() => {
              setModalState('form');
            }}
          />
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: (theme) =>
              modalState === 'image-capturing' || modalState === 'image-confirmation'
                ? theme.spacing(0)
                : theme.spacing(6),
            [theme.breakpoints.up('tablet')]: {
              p: theme.spacing(6),
            },
          }}
        >
          {!isGettingExamEventExaminee ? (
            <AlternativeRegistrationModalLogicContent
              state={modalState}
              form={formContext}
              imageData={image}
              cameraReady={!isCameraError}
              onRetryCamera={handleStartCamera}
              slotProps={{
                documentImageField: {
                  onClick: (_, reason) => {
                    if (reason === 'input') {
                      handleRetake();
                      setModalState('image-capturing');
                    } else if (reason === 'preview') {
                      openImagePreviewerModal();
                    }
                  },
                },
                error: alternativeRegistrationError,
              }}
            />
          ) : (
            <Loading />
          )}
          <Box
            sx={{
              display: modalState === 'image-capturing' && !isCameraError ? 'flex' : 'none',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              width: '100%',
              height: '100%',
            }}
          >
            <Camera
              width={752}
              height={658}
              borderType='round'
              facingMode={isDesktop ? 'user' : 'environment'}
              ref={cameraRef}
              visible={modalState === 'image-capturing' && !isCameraError}
              onCapture={async (blob) => {
                const watermarkedImage = await applyWatermark(blob, {
                  fontSize: 32,
                  horizontalSpacing: 16,
                  verticalSpacing: 24,
                });
                const imageUrl = URL.createObjectURL(watermarkedImage);
                // Release the previous capture's URL, unless it is the confirmed
                // one still shown in the form (that is revoked on exit instead).
                if (image && image !== formContext.getValues('documentImage')) {
                  URL.revokeObjectURL(image);
                }
                capturedImageBlobRef.current = watermarkedImage;
                setImage(imageUrl);
                setModalState('image-confirmation');
              }}
              onError={() => {
                setIsCameraError(true);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <AlternativeRegistrationModalLogicAction
            state={modalState}
            loading={formContext.formState.isSubmitting || isUploadingImage}
            onClose={handleClose}
            onSubmit={handleFormSubmit}
            captureImage={handleCaptureImage}
            cameraReady={!isCameraError}
            onRetryCamera={() => {
              handleRetake();
              setModalState('image-capturing');
            }}
            onConfirmImage={() => {
              // Commit the confirmed image and its binary together so they stay
              // in sync (retake + Back can't leave a stale blob behind).
              setDocumentImageBlob(capturedImageBlobRef.current ?? undefined);
              formContext.setValue('documentImage', image ?? '', {
                shouldDirty: true,
                shouldValidate: true,
              });
              setModalState('form');
            }}
          />
        </DialogActions>
      </Dialog>

      {/* Preview Image Modal — shows the confirmed image, not a pending retake */}
      <PreviewImageModal
        open={isImagePreviewerModalOpen}
        image={formContext.watch('documentImage')}
        onClose={closeImagePreviewerModal}
      />
    </>
  );
}
